import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import './MsaForm.css';

const defaultVars = {
  billing_freq: 'Monthly', payment_terms: 30, late_interest_pct: 1.5,
  initial_term_years: 1, renewal_notice_days: 30, termination_notice_days: 30,
  cure_period_days: 15, confidentiality_years: 5, non_solicit_months: 12,
  conversion_fee_pct: 25, liability_cap_months: 12, governing_law: 'Republic of India',
  negotiation_days: 30, arbitration_seat: 'Chennai', arbitration_rules: 'SIAC Rules',
  audit_notice_period: '30 days', audit_frequency: 'Annually', audit_cost_resp: 'Client',
  initial_fixed_period: '12 months', annual_escalation_pct: 5,
  escalation_eff_date: '', scope_impact_params: 'Cost, Timeline',
  impact_assess_period: '5 days', authorized_approver: 'Both Parties'
};

export default function MsaForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEdit = !!id;

  const [form, setForm] = useState({
    msa_ref: '', version: 1.0, customer_id: '', effective_date: '', start_date: '', end_date: '',
    status: 'Draft', provider_signatory_id: '', provider_address_id: '',
    billing_mode: 'separate', uploaded_agreement_path: '',
    max_billing_hours_per_day: '',
    parent_msa_id: null, new_version_effective_from: '', ...defaultVars
  });
  const [dynFields, setDynFields] = useState([]);
  const [kfv, setKfv] = useState({});
  const [customers, setCustomers] = useState([]);
  const [clauseTitles, setClauseTitles] = useState([]);
  const [selectedClauses, setSelectedClauses] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [provider, setProvider] = useState({});
  const [customer, setCustomer] = useState({});
  const [signatories, setSignatories] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [billingGroups, setBillingGroups] = useState([]);
  const [newBgName, setNewBgName] = useState('');
  const [subBrands, setSubBrands] = useState([]);
  const [newSbName, setNewSbName] = useState('');
  const [selectedSig, setSelectedSig] = useState(null);
  const [selectedAddr, setSelectedAddr] = useState(null);
  const [versionModal, setVersionModal] = useState(null);

  useEffect(() => {
    api.get('/customers').then(rows => setCustomers(Array.isArray(rows) ? rows : []));
    api.get('/settings/provider').then(setProvider);
    api.get('/settings/signatories').then(s =>
      setSignatories(Array.isArray(s) ? s.filter(x => x.is_active) : []));
    api.get('/settings/addresses').then(a =>
      setAddresses(Array.isArray(a) ? a.filter(x => x.is_active) : []));
    api.get('/key-fields').then(kf => setDynFields(Array.isArray(kf) ? kf.filter(f => f.is_active && (f.applies_to === 'MSA' || f.applies_to === 'Both')) : []));
    api.get('/clauses/msa/titles').then(titles => {
      const list = Array.isArray(titles) ? titles : [];
      setClauseTitles(list);
      if (!id) {
        setSelectedClauses(list.map(t => ({
          clause_title: t.clause_title, selected_template: (Array.isArray(t.templates) && t.templates[0]?.template_name) || '',
          sort_order: t.sort_order, show: t.is_mandatory ? 1 : 1
        })));
      }
    }).catch(() => {
      setClauseTitles([]);
      if (!id) setSelectedClauses([]);
    });
  }, [id]);

  useEffect(() => {
    if (id) {
      api.get(`/msa/${id}`).then(data => {
        if (!data || typeof data !== 'object') return;
        setForm({
          msa_ref: data.msa_ref || '', version: data.version, customer_id: data.customer_id,
          effective_date: data.effective_date || '', start_date: data.start_date || '', end_date: data.end_date || '',
          status: data.status, provider_signatory_id: data.provider_signatory_id || '',
          provider_address_id: data.provider_address_id || '', billing_mode: data.billing_mode || 'separate',
          uploaded_agreement_path: data.uploaded_agreement_path || '',
          max_billing_hours_per_day: data.max_billing_hours_per_day ?? '',
          billing_freq: data.billing_freq, payment_terms: data.payment_terms,
          late_interest_pct: data.late_interest_pct, initial_term_years: data.initial_term_years,
          renewal_notice_days: data.renewal_notice_days, termination_notice_days: data.termination_notice_days,
          cure_period_days: data.cure_period_days, confidentiality_years: data.confidentiality_years,
          non_solicit_months: data.non_solicit_months, conversion_fee_pct: data.conversion_fee_pct,
          liability_cap_months: data.liability_cap_months, governing_law: data.governing_law,
          negotiation_days: data.negotiation_days, arbitration_seat: data.arbitration_seat,
          arbitration_rules: data.arbitration_rules, audit_notice_period: data.audit_notice_period,
          audit_frequency: data.audit_frequency, audit_cost_resp: data.audit_cost_resp,
          initial_fixed_period: data.initial_fixed_period, annual_escalation_pct: data.annual_escalation_pct,
          escalation_eff_date: data.escalation_eff_date || '', scope_impact_params: data.scope_impact_params,
          impact_assess_period: data.impact_assess_period, authorized_approver: data.authorized_approver,
          parent_msa_id: data.parent_msa_id || null, new_version_effective_from: data.new_version_effective_from || ''
        });
        if (data.key_field_values && typeof data.key_field_values === 'object') setKfv(data.key_field_values);
        if (Array.isArray(data.clauses) && data.clauses.length) setSelectedClauses(data.clauses);
        if (data.provider) setProvider(data.provider);
        if (Array.isArray(data.billing_groups)) setBillingGroups(data.billing_groups);
        if (Array.isArray(data.sub_brands)) setSubBrands(data.sub_brands);
        setCustomer({ legal_name: data.client_name, registered_address: data.client_address, country: data.client_country, auth_signatory: data.client_signatory, signatory_title: data.client_title, gst_vat: data.client_gst });
        if (data.provider_signatory_name) setSelectedSig({ name: data.provider_signatory_name, title: data.provider_signatory_title });
        if (data.provider_address_title) setSelectedAddr({ address_title: data.provider_address_title, address_line: data.provider_address_line, city: data.provider_city, state: data.provider_state });
      }).catch(() => {});
    }
  }, [id]);

  useEffect(() => {
    if (form.customer_id) {
      const c = customers.find(x => x.customer_id === form.customer_id);
      if (c) setCustomer(c);
    }
  }, [form.customer_id, customers]);

  useEffect(() => {
    if (form.provider_signatory_id) {
      const s = signatories.find(x => x.id === parseInt(form.provider_signatory_id));
      if (s) setSelectedSig(s);
    }
  }, [form.provider_signatory_id, signatories]);

  useEffect(() => {
    if (form.provider_address_id) {
      const a = addresses.find(x => x.id === parseInt(form.provider_address_id));
      if (a) setSelectedAddr(a);
    }
  }, [form.provider_address_id, addresses]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const updateClause = (idx, field, value) => {
    setSelectedClauses(prev => (Array.isArray(prev) ? prev : []).map((c, i) => i === idx ? { ...c, [field]: value } : c));
  };

  const resolveTemplate = (clauseTitle, templateName) => {
    const titles = Array.isArray(clauseTitles) ? clauseTitles : [];
    const ct = titles.find(c => c.clause_title === clauseTitle);
    const tpls = ct && Array.isArray(ct.templates) ? ct.templates : [];
    return tpls.find(t => t.template_name === templateName);
  };

  const substituteVars = (text) => {
    if (!text) return '';
    const vars = {
      ...kfv,
      provider_name: provider.provider_name || '', client_name: customer.legal_name || '',
      billing_frequency: form.billing_freq || '', payment_terms: form.payment_terms || '',
      late_interest_pct: form.late_interest_pct || '', initial_term_years: form.initial_term_years || '',
      renewal_notice_days: form.renewal_notice_days || '', termination_notice_days: form.termination_notice_days || '',
      cure_period_days: form.cure_period_days || '', confidentiality_years: form.confidentiality_years || '',
      non_solicit_months: form.non_solicit_months || '', conversion_fee_pct: form.conversion_fee_pct || '',
      liability_cap_months: form.liability_cap_months || '', governing_law: form.governing_law || '',
      negotiation_days: form.negotiation_days || '', arbitration_seat: form.arbitration_seat || '',
      arbitration_rules: form.arbitration_rules || '', audit_notice_period: form.audit_notice_period || '',
      audit_frequency: form.audit_frequency || '', audit_cost_resp: form.audit_cost_resp || '',
      initial_fixed_period: form.initial_fixed_period || '', annual_escalation_pct: form.annual_escalation_pct || '',
      escalation_eff_date: form.escalation_eff_date || '', scope_impact_params: form.scope_impact_params || '',
      impact_assess_period: form.impact_assess_period || '', authorized_approver: form.authorized_approver || '',
    };
    return text.replace(/\{\{(\w+)\}\}/g, (_, key) => vars[key] !== undefined ? vars[key] : `{{${key}}}`);
  };

  const previewClauses = useMemo(() => {
    const rows = Array.isArray(selectedClauses) ? selectedClauses : [];
    return rows.filter(c => c.show).sort((a, b) => a.sort_order - b.sort_order).map(c => {
      const tpl = resolveTemplate(c.clause_title, c.selected_template);
      return { title: c.clause_title, text: tpl ? substituteVars(tpl.template_text) : 'Template not found' };
    });
  }, [selectedClauses, clauseTitles, form, provider, customer, kfv]);

  const groupedDynFieldEntries = useMemo(() => {
    if (!Array.isArray(dynFields) || dynFields.length === 0) return [];
    const acc = {};
    for (const f of dynFields) {
      if (!f || typeof f !== 'object') continue;
      const g = f.field_group || 'Other';
      if (!acc[g]) acc[g] = [];
      acc[g].push(f);
    }
    return Object.entries(acc);
  }, [dynFields]);

  const saveMsa = async () => {
    const payload = { ...form, clauses: selectedClauses, billing_groups: billingGroups, sub_brands: subBrands, key_field_values: kfv };
    if (isEdit) await api.put(`/msa/${id}`, payload);
    else await api.post('/msa', payload);
  };

  const openNewVersion = () => setVersionModal({
    version_comments: '', new_version_effective_from: '', duplicate_sows: false,
    created_on: new Date().toISOString().slice(0, 10)
  });

  const submitNewVersion = async () => {
    if (!versionModal.version_comments.trim()) { alert('Version Comments are required.'); return; }
    try {
      const r = await api.post(`/msa/${id}/new-version`, {
        new_version_effective_from: versionModal.new_version_effective_from || null,
        version_comments: versionModal.version_comments,
        duplicate_sows: versionModal.duplicate_sows
      });
      const sowMsg = r.duplicated_sows ? ` Also duplicated ${r.duplicated_sows} SOW(s) as drafts.` : '';
      alert(`Created ${r.msa_id} (v${r.version}) as Draft.${sowMsg}`);
      setVersionModal(null);
      navigate(`/msa/${r.msa_id}`);
    } catch (e) { alert(e.message); }
  };

  const handleUnconfirm = async () => {
    if (!window.confirm('Mark this MSA as Unconfirmed? Child SOWs will also be marked Unconfirmed.')) return;
    try { await api.post(`/msa/${id}/unconfirm`, {}); alert('Marked Unconfirmed.'); navigate('/msa'); }
    catch (e) { alert(e.message); }
  };

  const handleSave = async () => {
    try {
      await saveMsa();
      navigate('/msa');
    } catch (e) { alert(e.message); }
  };

  const handleSubmit = async () => {
    if (!window.confirm('Submit this MSA for approval? It will be locked for editing.')) return;
    try {
      await saveMsa();
      await api.post('/approvals/submit', { entity_type: 'MSA', entity_id: id });
      navigate('/msa');
    } catch (e) { alert(e.message); }
  };

  const addBillingGroup = () => {
    if (!newBgName.trim()) return;
    setBillingGroups(prev => [...prev, { group_name: newBgName.trim(), description: '', team_lead_emp_id: '', team_lead_name: '', team_lead_designation: '' }]);
    setNewBgName('');
  };

  const updateBillingGroup = (idx, field, value) => {
    setBillingGroups(prev => prev.map((bg, i) => i === idx ? { ...bg, [field]: value } : bg));
  };

  const removeBillingGroup = (idx) => setBillingGroups(prev => prev.filter((_, i) => i !== idx));

  const addSubBrand = () => {
    if (!newSbName.trim()) return;
    setSubBrands(prev => [...prev, { brand_name: newSbName.trim(), description: '', is_active: 1 }]);
    setNewSbName('');
  };
  const updateSubBrand = (idx, field, value) => setSubBrands(prev => prev.map((sb, i) => i === idx ? { ...sb, [field]: value } : sb));
  const removeSubBrand = (idx) => setSubBrands(prev => prev.filter((_, i) => i !== idx));

  const isLocked = form.status === 'Submitted' || form.status === 'Approved' || form.status === 'Active';

  const displayTitle = isEdit ? (form.msa_ref?.trim() ? form.msa_ref.trim() : id) : 'New MSA';

  const canSaveMsa = !isLocked;
  const canSubmitApproval = isEdit && (form.status === 'Draft' || form.status === 'Rejected');
  const canCreateNewVersion =
    isEdit && ['Active', 'Approved', 'Expired', 'Terminated'].includes(form.status) && user?.role === 'Admin';
  const showMsaActionsBar = canSaveMsa || canSubmitApproval || canCreateNewVersion;

  return (
    <>
      <div className="page-header">
        <h1>MSA Agreements</h1>
      </div>
      <div className="page-content msa-form-page">
        <div className="msa-form-toolbar">
          <div className="msa-form-toolbar-top">
            <nav className="page-back-nav" aria-label="Back navigation">
              <Link to="/msa" className="page-back-link">
                ← Back to MSA agreements
              </Link>
            </nav>
            {showMsaActionsBar && (
              <div className="msa-form-toolbar-actions" role="group" aria-label="MSA actions">
                {canSaveMsa && (
                  <button type="button" className="btn btn-primary" onClick={handleSave}>
                    Save MSA
                  </button>
                )}
                {canSubmitApproval && (
                  <button type="button" className="btn btn-primary" onClick={handleSubmit}>
                    Submit for Approval
                  </button>
                )}
                {canCreateNewVersion && (
                  <button type="button" className="btn btn-secondary" onClick={openNewVersion}>
                    + Create New Version
                  </button>
                )}
              </div>
            )}
          </div>

          <header className="msa-form-intro">
            <h2 className="msa-form-intro-title">{displayTitle}</h2>
            <p className="msa-form-intro-sub">
              {isEdit
                ? customer.legal_name
                  ? (
                    <>
                      <strong>{customer.legal_name}</strong>
                      {form.customer_id ? <span className="msa-form-intro-cust-id"> · {form.customer_id}</span> : null}
                    </>
                  )
                  : 'Loading customer…'
                : 'Choose customer, dates, and billing mode — then clauses and live preview.'}
            </p>
          </header>
        </div>
        {isEdit ? (
          <section className="msa-form-glance" aria-labelledby="msa-glance-heading">
            <h2 id="msa-glance-heading" className="msa-form-glance-title">
              At a glance
            </h2>
            <div className="msa-form-summary" aria-label="MSA summary">
            <div className="msa-form-summary-card">
              <div className="msa-form-summary-card-label">Status</div>
              <div className="msa-form-summary-card-value">
                <span className={`badge badge-${form.status?.toLowerCase()}`}>{form.status}</span>
              </div>
            </div>
            <div className="msa-form-summary-card">
              <div className="msa-form-summary-card-label">Version</div>
              <div className="msa-form-summary-card-value tabular-nums">{form.version}</div>
            </div>
            <div className="msa-form-summary-card">
              <div className="msa-form-summary-card-label">MSA ID</div>
              <div className="msa-form-summary-card-value tabular-nums msa-form-summary-card-value-wrap">{id}</div>
            </div>
            {form.msa_ref?.trim() && form.msa_ref.trim() !== id && (
              <div className="msa-form-summary-card">
                <div className="msa-form-summary-card-label">Reference</div>
                <div className="msa-form-summary-card-value msa-form-summary-card-value-wrap">{form.msa_ref.trim()}</div>
              </div>
            )}
            <div className="msa-form-summary-card">
              <div className="msa-form-summary-card-label">Customer</div>
              <div
                className={`msa-form-summary-card-value ${customer.legal_name?.length > 36 ? 'msa-form-summary-card-value-wrap' : ''}`}
              >
                {customer.legal_name || '—'}
              </div>
            </div>
            <div className="msa-form-summary-card">
              <div className="msa-form-summary-card-label">Term</div>
              <div className="msa-form-summary-card-value msa-form-summary-card-value-wrap">
                {form.effective_date || form.start_date || form.end_date
                  ? `${form.effective_date || form.start_date || '—'} → ${form.end_date || '—'}`
                  : '—'}
              </div>
            </div>
            {isLocked && (
              <div className="msa-form-summary-card">
                <div className="msa-form-summary-card-label">Edits</div>
                <div className="msa-form-summary-card-value">
                  <span className="badge badge-submitted">Read-only</span>
                </div>
              </div>
            )}
            </div>
          </section>
        ) : (
          <p className="msa-form-new-hint">
            Create a new Master Service Agreement: choose a customer, set dates and billing mode, then configure clauses and variables.
            Use <strong>Prepare Agreement</strong> for a live preview with variable substitution.
          </p>
        )}

        <div className="split-panel msa-form-split">
          <div className="left-panel">
            {/* Agreement Controls - FIRST */}
            <div className="card">
              <div className="card-header">Agreement Controls</div>
              <div className="msa-form-agreement-controls btn-group">
                <button
                  type="button"
                  className={`btn ${showPreview ? 'btn-success' : 'btn-primary'}`}
                  onClick={() => setShowPreview(!showPreview)}
                >
                  {showPreview ? 'Preview Active' : 'Prepare Agreement'}
                </button>
                <label className="btn btn-secondary msa-form-upload-label">
                  Upload Customer Agreement
                  <input
                    type="file"
                    hidden
                    onChange={e => {
                      const f = e.target.files[0];
                      if (f) set('uploaded_agreement_path', f.name);
                    }}
                  />
                </label>
                {isEdit && user?.role === 'Admin' && form.status !== 'Unconfirmed' && (
                  <button type="button" className="btn btn-outline btn-sm" onClick={handleUnconfirm}>
                    Mark Unconfirmed
                  </button>
                )}
              </div>
              {form.uploaded_agreement_path && (
                <p className="msa-form-meta">
                  <strong>Uploaded:</strong> {form.uploaded_agreement_path}
                </p>
              )}
              {form.rejection_reason && (
                <p className="msa-form-meta msa-form-meta-danger">
                  <strong>Rejection reason:</strong> {form.rejection_reason}
                </p>
              )}
            </div>

            {/* Agreement ID */}
            <div className="card">
              <div className="card-header">Agreement Identification</div>
              <div className="form-grid">
                {isEdit && <div className="form-group"><label>MSA Number</label><input value={id} readOnly /></div>}
                <div className="form-group"><label>Reference Number</label><input value={form.msa_ref} onChange={e => set('msa_ref', e.target.value)} disabled={isLocked} /></div>
                <div className="form-group"><label>Version</label><input value={form.version} readOnly disabled /></div>
                <div className="form-group"><label>Customer *</label>
                  <select value={form.customer_id} onChange={e => set('customer_id', e.target.value)} disabled={isLocked}>
                    <option value="">Select Customer</option>
                    {customers.filter(c => c.status === 'Active').map(c => <option key={c.customer_id} value={c.customer_id}>{c.customer_id} - {c.legal_name}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Effective Date</label><input type="date" value={form.effective_date} onChange={e => set('effective_date', e.target.value)} disabled={isLocked} /></div>
                <div className="form-group"><label>Start Date</label><input type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} disabled={isLocked} /></div>
                <div className="form-group"><label>End Date</label><input type="date" value={form.end_date} onChange={e => set('end_date', e.target.value)} disabled={isLocked} /></div>
                {form.parent_msa_id && (
                  <>
                    <div className="form-group"><label>Parent MSA</label><input value={form.parent_msa_id} readOnly /></div>
                    <div className="form-group"><label>New-Version Effective From</label><input type="date" value={form.new_version_effective_from} onChange={e => set('new_version_effective_from', e.target.value)} disabled={isLocked} /></div>
                  </>
                )}
              </div>
            </div>

            {/* Provider */}
            <div className="card">
              <div className="card-header">Service Provider</div>
              <div className="form-grid">
                <div className="form-group"><label>Company</label><input value={provider.provider_name || ''} readOnly /></div>
                <div className="form-group"><label>Signatory</label>
                  <select value={form.provider_signatory_id} onChange={e => set('provider_signatory_id', e.target.value)} disabled={isLocked}>
                    <option value="">Select Signatory</option>
                    {signatories.map(s => <option key={s.id} value={s.id}>{s.name} - {s.title}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Address</label>
                  <select value={form.provider_address_id} onChange={e => set('provider_address_id', e.target.value)} disabled={isLocked}>
                    <option value="">Select Address</option>
                    {addresses.map(a => <option key={a.id} value={a.id}>{a.address_title} - {a.city}</option>)}
                  </select>
                </div>
                {selectedAddr && <div className="form-group full-width"><label>Selected Address</label><input value={`${selectedAddr.address_line}, ${selectedAddr.city || ''}, ${selectedAddr.state || ''}`} readOnly /></div>}
              </div>
            </div>

            {/* Client */}
            <div className="card">
              <div className="card-header">Client (from Customer Master)</div>
              <div className="form-grid">
                <div className="form-group"><label>Name</label><input value={customer.legal_name || ''} readOnly /></div>
                <div className="form-group"><label>Address</label><input value={customer.registered_address || ''} readOnly /></div>
                <div className="form-group"><label>Country</label><input value={customer.country || ''} readOnly /></div>
                <div className="form-group"><label>Signatory</label><input value={customer.auth_signatory || ''} readOnly /></div>
                <div className="form-group"><label>Title</label><input value={customer.signatory_title || ''} readOnly /></div>
              </div>
            </div>

            {/* Billing Mode */}
            <div className="card">
              <div className="card-header">Billing Configuration</div>
              <div className="form-grid">
                <div className="form-group"><label>Billing Mode</label>
                  <select value={form.billing_mode} onChange={e => set('billing_mode', e.target.value)} disabled={isLocked}>
                    <option value="separate">Bill each SOW separately</option>
                    <option value="grouped">Use Billing Groups</option>
                  </select>
                </div>
                <div className="form-group"><label>Max Billing Hours per Day (optional)</label>
                  <input type="number" step="0.25" min="0" max="24" value={form.max_billing_hours_per_day} onChange={e => set('max_billing_hours_per_day', e.target.value)} disabled={isLocked} />
                </div>
              </div>
              {form.billing_mode === 'grouped' && (
                <div className="mt-4">
                  <div className="msa-form-inline-add">
                    <input value={newBgName} onChange={e => setNewBgName(e.target.value)} placeholder="Group name" disabled={isLocked} />
                    {!isLocked && (
                      <button type="button" className="btn btn-primary btn-sm" onClick={addBillingGroup}>
                        Add Group
                      </button>
                    )}
                  </div>
                  {billingGroups.map((bg, i) => (
                    <div key={i} className="msa-form-nested-block">
                      <div className="msa-form-nested-block-header">
                        <span className="msa-form-nested-title">{bg.group_name}</span>
                        {!isLocked && (
                          <button type="button" className="btn btn-outline btn-sm" onClick={() => removeBillingGroup(i)}>
                            Remove
                          </button>
                        )}
                      </div>
                      <div className="form-grid">
                        <div className="form-group"><label>Team Lead Employee ID</label><input value={bg.team_lead_emp_id || ''} onChange={e => updateBillingGroup(i, 'team_lead_emp_id', e.target.value)} disabled={isLocked} /></div>
                        <div className="form-group"><label>Team Lead Name</label><input value={bg.team_lead_name || ''} onChange={e => updateBillingGroup(i, 'team_lead_name', e.target.value)} disabled={isLocked} /></div>
                        <div className="form-group"><label>Team Lead Designation</label><input value={bg.team_lead_designation || ''} onChange={e => updateBillingGroup(i, 'team_lead_designation', e.target.value)} disabled={isLocked} /></div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Sub-Brands (reference for Timesheet) */}
            <div className="card">
              <div className="card-header">Sub-Brands (for Timesheet reference)</div>
              <div className="msa-form-inline-add">
                <input value={newSbName} onChange={e => setNewSbName(e.target.value)} placeholder="Brand name" disabled={isLocked} />
                {!isLocked && (
                  <button type="button" className="btn btn-primary btn-sm" onClick={addSubBrand}>
                    Add Brand
                  </button>
                )}
              </div>
              {subBrands.map((sb, i) => (
                <div key={i} className="msa-form-nested-block">
                  <div className="msa-form-nested-brand-row">
                    <input
                      type="text"
                      value={sb.brand_name}
                      onChange={e => updateSubBrand(i, 'brand_name', e.target.value)}
                      disabled={isLocked}
                    />
                    <label className="msa-form-active-toggle">
                      <input
                        type="checkbox"
                        checked={sb.is_active !== 0}
                        onChange={e => updateSubBrand(i, 'is_active', e.target.checked ? 1 : 0)}
                        disabled={isLocked}
                      />
                      Active
                    </label>
                    {!isLocked && (
                      <button type="button" className="btn btn-outline btn-sm" onClick={() => removeSubBrand(i)}>
                        Delete
                      </button>
                    )}
                  </div>
                  <div className="form-group"><label>Description</label><input value={sb.description || ''} onChange={e => updateSubBrand(i, 'description', e.target.value)} disabled={isLocked} /></div>
                </div>
              ))}
            </div>

            {/* Clause Selection */}
            <div className="card">
              <div className="card-header">Clause Template Selection</div>
              <table className="clause-table">
                <thead><tr><th>Clause</th><th>Template</th><th>Sort</th><th>Show</th></tr></thead>
                <tbody>
                  {(Array.isArray(selectedClauses) ? selectedClauses : []).map((c, i) => {
                    const titles = Array.isArray(clauseTitles) ? clauseTitles : [];
                    const ct = titles.find(x => x.clause_title === c.clause_title);
                    const isMandatory = ct?.is_mandatory;
                    return (
                      <tr key={i}>
                        <td className="msa-form-clause-title">
                          {c.clause_title}
                          {isMandatory ? <span className="badge badge-approved msa-form-mandatory-mark">M</span> : null}
                        </td>
                        <td>
                          <select value={c.selected_template} onChange={e => updateClause(i, 'selected_template', e.target.value)} disabled={isLocked}>
                            {(Array.isArray(ct?.templates) ? ct.templates : []).map(t => <option key={t.template_name} value={t.template_name}>{t.template_name}</option>)}
                          </select>
                        </td>
                        <td>
                          <input
                            type="number"
                            className="msa-form-sort-input"
                            value={c.sort_order}
                            onChange={e => updateClause(i, 'sort_order', parseInt(e.target.value, 10))}
                            disabled={isLocked}
                          />
                        </td>
                        <td className="text-center">
                          <input type="checkbox" checked={!!c.show} onChange={e => updateClause(i, 'show', e.target.checked ? 1 : 0)} disabled={isLocked || isMandatory} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Clause Variables */}
            <div className="card">
              <div className="card-header">Clause Variables</div>
              <div className="form-grid">
                <div className="form-group"><label>Billing Frequency</label>
                  <select value={form.billing_freq} onChange={e => set('billing_freq', e.target.value)} disabled={isLocked}>
                    <option>Monthly</option><option>Quarterly</option><option>Bi-Weekly</option>
                  </select>
                </div>
                <div className="form-group"><label>Payment Terms (days)</label><input type="number" value={form.payment_terms} onChange={e => set('payment_terms', e.target.value)} disabled={isLocked} /></div>
                <div className="form-group"><label>Late Interest %</label><input type="number" step="0.1" value={form.late_interest_pct} onChange={e => set('late_interest_pct', e.target.value)} disabled={isLocked} /></div>
                <div className="form-group"><label>Initial Term (years)</label><input type="number" value={form.initial_term_years} onChange={e => set('initial_term_years', e.target.value)} disabled={isLocked} /></div>
                <div className="form-group"><label>Renewal Notice (days)</label><input type="number" value={form.renewal_notice_days} onChange={e => set('renewal_notice_days', e.target.value)} disabled={isLocked} /></div>
                <div className="form-group"><label>Termination Notice (days)</label><input type="number" value={form.termination_notice_days} onChange={e => set('termination_notice_days', e.target.value)} disabled={isLocked} /></div>
                <div className="form-group"><label>Cure Period (days)</label><input type="number" value={form.cure_period_days} onChange={e => set('cure_period_days', e.target.value)} disabled={isLocked} /></div>
                <div className="form-group"><label>Confidentiality (years)</label><input type="number" value={form.confidentiality_years} onChange={e => set('confidentiality_years', e.target.value)} disabled={isLocked} /></div>
                <div className="form-group"><label>Non-Solicitation (months)</label><input type="number" value={form.non_solicit_months} onChange={e => set('non_solicit_months', e.target.value)} disabled={isLocked} /></div>
                <div className="form-group"><label>Conversion Fee %</label><input type="number" value={form.conversion_fee_pct} onChange={e => set('conversion_fee_pct', e.target.value)} disabled={isLocked} /></div>
                <div className="form-group"><label>Liability Cap (months)</label><input type="number" value={form.liability_cap_months} onChange={e => set('liability_cap_months', e.target.value)} disabled={isLocked} /></div>
                <div className="form-group"><label>Governing Law</label><input value={form.governing_law} onChange={e => set('governing_law', e.target.value)} disabled={isLocked} /></div>
                <div className="form-group"><label>Negotiation (days)</label><input type="number" value={form.negotiation_days} onChange={e => set('negotiation_days', e.target.value)} disabled={isLocked} /></div>
                <div className="form-group"><label>Arbitration Seat</label><input value={form.arbitration_seat} onChange={e => set('arbitration_seat', e.target.value)} disabled={isLocked} /></div>
                <div className="form-group"><label>Arbitration Rules</label><input value={form.arbitration_rules} onChange={e => set('arbitration_rules', e.target.value)} disabled={isLocked} /></div>
                <div className="form-group"><label>Audit Notice Period</label><input value={form.audit_notice_period} onChange={e => set('audit_notice_period', e.target.value)} disabled={isLocked} /></div>
                <div className="form-group"><label>Audit Frequency</label><input value={form.audit_frequency} onChange={e => set('audit_frequency', e.target.value)} disabled={isLocked} /></div>
                <div className="form-group"><label>Audit Cost Responsibility</label><input value={form.audit_cost_resp} onChange={e => set('audit_cost_resp', e.target.value)} disabled={isLocked} /></div>
                <div className="form-group"><label>Initial Fixed Period</label><input value={form.initial_fixed_period} onChange={e => set('initial_fixed_period', e.target.value)} disabled={isLocked} /></div>
                <div className="form-group"><label>Annual Escalation %</label><input type="number" value={form.annual_escalation_pct} onChange={e => set('annual_escalation_pct', e.target.value)} disabled={isLocked} /></div>
                <div className="form-group"><label>Escalation Eff. Date</label><input type="date" value={form.escalation_eff_date} onChange={e => set('escalation_eff_date', e.target.value)} disabled={isLocked} /></div>
                <div className="form-group"><label>Scope Impact Params</label><input value={form.scope_impact_params} onChange={e => set('scope_impact_params', e.target.value)} disabled={isLocked} /></div>
                <div className="form-group"><label>Impact Assess Period</label><input value={form.impact_assess_period} onChange={e => set('impact_assess_period', e.target.value)} disabled={isLocked} /></div>
                <div className="form-group"><label>Authorized Approver</label><input value={form.authorized_approver} onChange={e => set('authorized_approver', e.target.value)} disabled={isLocked} /></div>
              </div>
            </div>

            {/* Other Details (from Key Fields master) */}
            {groupedDynFieldEntries.length > 0 && (
              <div className="card">
                <div className="card-header">Other Details</div>
                {groupedDynFieldEntries.map(([group, fields]) => (
                  <div key={group} className="msa-form-key-field-group-wrap">
                    <h3 className="msa-form-key-field-group-title">{group}</h3>
                    <div className="form-grid">
                      {fields.map(f => (
                        <div key={f.id} className={f.field_type === 'textarea' ? 'form-group full-width' : 'form-group'}>
                          <label>{f.field_label}</label>
                          {f.field_type === 'textarea'
                            ? <textarea value={kfv[f.field_name] || ''} onChange={e => setKfv(p => ({ ...p, [f.field_name]: e.target.value }))} disabled={isLocked} />
                            : <input type={f.field_type === 'number' ? 'number' : f.field_type === 'date' ? 'date' : 'text'} value={kfv[f.field_name] || ''} onChange={e => setKfv(p => ({ ...p, [f.field_name]: e.target.value }))} disabled={isLocked} />}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right panel - Live preview */}
          <div className="msa-form-preview-rail">
            {showPreview ? (
              <div className="agreement-preview">
                <div className="msa-form-preview-logo">
                  <img src="/logo.png" alt="Colan" />
                </div>
                <h1>MASTER SERVICES AGREEMENT</h1>
                <p className="agreement-subtitle">
                  Effective as of {form.effective_date || '___'} between {provider.provider_name || '___'} (Provider)
                  and {customer.legal_name || '___'} (Client)
                </p>

                {previewClauses.map((c, i) => (
                  <div key={i}>
                    <h3>{i + 1}. {c.title}</h3>
                    <p>{c.text}</p>
                  </div>
                ))}

                <div className="signature-block">
                  <div className="sig-party">
                    <strong>For: {provider.provider_name}</strong>
                    <div>Name: {selectedSig?.name || '___'}</div>
                    <div>Title: {selectedSig?.title || '___'}</div>
                    <div className="sig-line"></div>
                    <div>Signature</div>
                  </div>
                  <div className="sig-party">
                    <strong>For: {customer.legal_name}</strong>
                    <div>Name: {customer.auth_signatory}</div>
                    <div>Title: {customer.signatory_title}</div>
                    <div className="sig-line"></div>
                    <div>Signature</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="agreement-preview">
                <div className="agreement-placeholder">
                  <h3>Live MSA Preview</h3>
                  <p>Click &quot;Prepare Agreement&quot; to generate the agreement preview.</p>
                  <p className="msa-form-placeholder-note">
                    Clause templates and variable values are substituted in real time while you edit.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

      </div>

      {versionModal && (
        <div className="modal-overlay" onClick={() => setVersionModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Create New MSA Version</h2>
            <p className="msa-form-modal-hint">
              A new draft will be created with values copied from <strong>{id}</strong>. The new version number is auto-assigned (max version in this MSA&apos;s lineage + 1).
            </p>
            <div className="form-grid">
              <div className="form-group full-width"><label>Version Comments *</label>
                <textarea rows={3} value={versionModal.version_comments} onChange={e => setVersionModal(m => ({ ...m, version_comments: e.target.value }))} placeholder="Reason for new version" />
              </div>
              <div className="form-group"><label>Created On</label><input value={versionModal.created_on} readOnly disabled /></div>
              <div className="form-group"><label>New-Version Effective From</label><input type="date" value={versionModal.new_version_effective_from} onChange={e => setVersionModal(m => ({ ...m, new_version_effective_from: e.target.value }))} /></div>
              <div className="form-group full-width">
                <label className="msa-form-modal-check-label">
                  <input type="checkbox" checked={versionModal.duplicate_sows} onChange={e => setVersionModal(m => ({ ...m, duplicate_sows: e.target.checked }))} />
                  <span>Also create duplicate SOWs (linked to this new MSA version, in Draft)</span>
                </label>
                <p className="msa-form-modal-checkbox-hint">
                  Each new MSA version should have a corresponding new SOW. Once approved, child SOWs become usable.
                </p>
              </div>
            </div>
            <div className="btn-group mt-4">
              <button className="btn btn-primary" onClick={submitNewVersion}>Create New Version</button>
              <button className="btn btn-secondary" onClick={() => setVersionModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
